"use client";

import React from "react";
import Link from "next/link";
import { useState } from "react";
import { Menu as MenuIcon } from "lucide-react";
import { usePathname } from "next/navigation";

import { Button } from "@/components/ui/button";
import { NavigationMenuLink } from "@/components/ui/navigation-menu";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
	Menu,
	MenuItem,
} from "@/components/custom/navigation-menu";
import { NavigationMenu } from "@radix-ui/react-navigation-menu";
import { MagneticButton } from "@/components/custom/magnetic-button";
import { BrandCard } from "@/components/custom/brand-card";

const SiteHeader = () => {
	const [active, setActive] = useState<string | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const pathname = usePathname();

	const NAVIGATION_DATA = {
		brands: [
			{
				title: "Elec3D",
				href: "/brands/elec3d" as const,
				description: "Custom 3D prints, drones & digital products",
				src: "/nav_elec3d.jpg",
				target: "_self",
				variant: "tech" as const,
			},
			{
				title: "TheHighraysYT",
				href: "https://www.thehighraysyt.com",
				description: "YouTube promotion services",
				src: "/nav_thehighraysyt.jpg",
				target: "_blank",
				variant: "youtube" as const,
			},
		],
	};

	const getHeaderConfig = () => {
		if (pathname === "/brands/elec3d") {
			return { className: "bg-[#f0f0f0] text-black", isDark: false };
		}
		if (pathname === "/contact") {
			return { className: "bg-[#0b0b0d] text-white", isDark: true };
		}
		if (pathname === "/brands/thehighraysyt" || pathname === "/about") {
			return {
				className: active
					? "bg-zinc-950 text-white"
					: "bg-zinc-950/80 backdrop-blur-md text-white",
				isDark: true
			};
		}
		return {
			className: active
				? "bg-background"
				: "bg-background/80 backdrop-blur-md supports-backdrop-filter:bg-background/60",
			isDark: false
		};
	};

	const { className: headerClass, isDark } = getHeaderConfig();

	return (
		<header
			className={cn(
				"fixed top-0 z-50 w-full transition-colors duration-300",
				headerClass
			)}
			role="banner"
		>
			<div className="flex h-20 w-full items-center justify-between px-4 md:px-10">
				<div className="flex items-center">
					<Link href="/" className="mr-6 flex items-center space-x-1" aria-label="TheHighrays - Go to homepage">
						<span className="font-syne text-2xl font-bold uppercase tracking-tight">
							TheHighrays
						</span>
					</Link>
				</div>

				<NavigationMenu className="hidden h-full items-center gap-8 md:flex" aria-label="Main navigation">
					<NavigationMenuLink asChild>
						<Link
							href="/"
							prefetch={true}
							className="font-syne text-sm font-bold uppercase tracking-wide hover:text-primary"
						>
							Home
						</Link>
					</NavigationMenuLink>

					<Menu setActive={setActive}>
						<MenuItem
							setActive={setActive}
							active={active}
							item="Our Brands"
							fullWidth
							dropdownClassName={isDark ? "dark bg-zinc-950 border-zinc-800" : ""}
						>
							<div className="container mx-auto grid grid-cols-12 gap-8 py-12 px-4 md:px-10">
								<div className="col-span-12 md:col-span-4 md:col-start-2 flex flex-col justify-between">
									<div className="space-y-4">
										<h2 className="font-syne text-4xl font-bold uppercase leading-none tracking-tight text-foreground">
											Our <br />
											<span className="text-primary">Brands</span>
										</h2>
										<p className="max-w-[300px] font-inter text-sm leading-relaxed text-muted-foreground">
											Explore our ecosystem of digital services and platforms designed to elevate your presence.
										</p>
									</div>
									<div className="mt-8 hidden md:block">
										<Link
											href="/#brands"
											className="group flex items-center gap-2 font-syne text-sm font-bold uppercase tracking-wide text-primary transition-colors hover:text-primary/80"
										>
											View All Brands
											<span className="transition-transform group-hover:translate-x-1">
												→
											</span>
										</Link>
									</div>
								</div>

								<div className="col-span-12 md:col-span-6 md:col-start-7 grid grid-cols-1 md:grid-cols-2 gap-6">
									{NAVIGATION_DATA.brands.map((brand) => (
										<BrandCard
											key={brand.title}
											title={brand.title}
											href={brand.href}
											description={brand.description}
											variant={brand.variant}
											className="h-full min-h-[300px]"
											target={brand.target as React.HTMLAttributeAnchorTarget}
										/>
									))}
								</div>
							</div>
						</MenuItem>
					</Menu>

					<NavigationMenuLink asChild>
						<Link
							href="/about"
							prefetch={true}
							className="font-syne text-sm font-bold uppercase tracking-wide hover:text-primary"
						>
							About
						</Link>
					</NavigationMenuLink>
				</NavigationMenu>

				<div className="hidden items-center gap-4 md:flex">
					<Link href="/contact">
						<MagneticButton className="rounded-sm bg-primary px-6 py-2 font-syne text-sm font-bold uppercase text-primary-foreground transition-transform hover:scale-105">
							Contact Us
						</MagneticButton>
					</Link>
				</div>

				{/* Mobile View */}
				<div className="flex items-center gap-2 md:hidden">
					<Sheet open={isOpen} onOpenChange={setIsOpen}>
						<SheetTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className={cn("md:hidden", isDark && "text-white hover:bg-white/10 hover:text-white")}
							>
								<MenuIcon className="h-6 w-6" />
								<span className="sr-only">Toggle menu</span>
							</Button>
						</SheetTrigger>
						<SheetContent
							side="right"
							className="w-screen h-screen border-none bg-background/95 px-4 backdrop-blur-3xl"
						>
							<SheetHeader className="px-0 pb-8 pt-4">
								<SheetTitle className="flex items-center w-full overflow-hidden text-left">
									<span className="font-syne text-2xl font-bold uppercase truncate">
										TheHighrays
									</span>
								</SheetTitle>
							</SheetHeader>
							<nav className="flex h-full flex-col gap-8 overflow-y-auto pb-20">
								<div className="flex flex-col gap-6">
									<Link
										href="/"
										prefetch={true}
										onClick={() => setIsOpen(false)}
										className="font-syne text-5xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
									>
										Home
									</Link>
									<Link
										href="/about"
										prefetch={true}
										onClick={() => setIsOpen(false)}
										className="font-syne text-5xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
									>
										About
									</Link>
									<Link
										href="/contact"
										prefetch={true}
										onClick={() => setIsOpen(false)}
										className="font-syne text-5xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
									>
										Contact Us
									</Link>
								</div>

								<div className="space-y-6 mt-4">
									<p className="font-syne text-sm font-bold uppercase text-muted-foreground tracking-widest">
										Our Brands
									</p>
									<div className="flex flex-col gap-6">
										{NAVIGATION_DATA.brands.map((brand) => (
											<Link
												key={brand.title}
												href={brand.href as "/brands/elec3d"}
												prefetch={true}
												onClick={() => setIsOpen(false)}
												className="font-syne text-3xl sm:text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors break-words"
												target={brand.target as React.HTMLAttributeAnchorTarget}
											>
												{brand.title}
											</Link>
										))}
									</div>
								</div>

								<div className="mt-auto w-full pt-8 pb-8">
									<Link
										href="/contact"
										prefetch={true}
										onClick={() => setIsOpen(false)}
										className="group relative flex w-full items-center justify-between overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 px-6 py-5 backdrop-blur-md transition-all hover:bg-primary/10 hover:border-primary/40 hover:shadow-[0_0_30px_-10px_var(--primary)] active:scale-[0.98]"
									>
										<div className="flex flex-col">
											<span className="font-syne text-lg font-bold uppercase text-foreground group-hover:text-primary transition-colors">
												Start a Project
											</span>
											<span className="font-inter text-xs text-muted-foreground">
												Get in touch with us
											</span>
										</div>
										<span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:scale-110 group-hover:-rotate-45 shadow-lg shadow-primary/20">
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
												<path d="M5 12h14" />
												<path d="M12 5l7 7-7 7" />
											</svg>
										</span>
									</Link>
								</div>
							</nav>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</header>
	);
};

export default SiteHeader;
